import { allAmountsSame, getHighestAmountIndex } from '@afterpay/utils'
import { CheckoutPaymentSchedule, Cohort, Country, FirstPaymentType } from '@afterpaytouch/portal-api'
import { useIsDesignLanguage, DesignLanguage } from '@afterpaytouch/core'
import { useTranslation } from 'next-i18next'
import {
  useAppRouter,
  useCalculatedPaymentSchedules,
  useCheckoutState,
  useCohort,
  useCountry,
  useGetPreferredPaymentDayQuery,
  useIsBillingAgreementApprovalProcess,
  useIsConsumerLendingOrder,
} from '..'
import { Features, money } from '../../model'
import { useFlag } from '@afterpaytouch/feature'
import { useMoney } from '../../component/Money/useMoney'

// Note:
// Variant E: 'current ap v0' - use in US Bronte design language, After 12 Jan 2026 we should remove this variant, Buckle I'm looking at you :eyes:
// Variant C: 'legacy' - use outside of US
// 'ga launch v2' summary is exclusive to Arcade design language, see ArcadeSummary

interface SummaryLayoutHook {
  variant: 'E' | 'C'
  showProfileCard: boolean
}

export const useSummaryLayout = (): SummaryLayoutHook => {
  // Note: Exclusive to Bronte Design Language
  const isConsumerLending = useIsConsumerLendingOrder()
  const isBronteDesign = useIsDesignLanguage(DesignLanguage.Bronte)
  const country = useCountry()
  const isUS = country === Country.US

  const conditions = [!isConsumerLending, isBronteDesign, isUS]
  const isEligible = conditions.every(Boolean)

  if (!isEligible) {
    return { variant: 'C', showProfileCard: true }
  }

  return { variant: 'E', showProfileCard: false }
}

export const useSchedulePreviewText = (): string => {
  const country = useCountry()

  const isBillingAgreementApprovalProcess = useIsBillingAgreementApprovalProcess()
  const paymentSchedules = useCalculatedPaymentSchedules()
  const { locale } = useAppRouter()
  const installments = paymentSchedules.map((schedule) => schedule.amount)
  const { firstPaymentType } = useCheckoutState()
  const isNpuf = firstPaymentType === FirstPaymentType.NoPaymentUpfront
  const shouldUseZeroMoneyInstallment = isNpuf || isBillingAgreementApprovalProcess
  const zeroMoney = money('0.00', installments[0]?.currency)
  const [amountDueToday] = shouldUseZeroMoneyInstallment ? [zeroMoney] : installments
  const { formatMoney } = useMoney({
    decimalPlaces: 2,
    currency: amountDueToday.currency,
    locale,
  })

  const isPPDEnabled = useFlag(Features.ENABLE_ALIGN_PAYMENT_DAY)
  const { data: ppdData } = useGetPreferredPaymentDayQuery(undefined, { skip: country !== Country.US || !isPPDEnabled })
  const { t } = useTranslation()

  const hasActivePPD = isPPDEnabled && country === Country.US && ppdData?.isEligible && !!ppdData?.preferredPaymentDay
  const cohort = useCohort()
  const isAPOrNetNew = cohort === Cohort.APOnly || cohort === Cohort.NetNew

  if (isBillingAgreementApprovalProcess) {
    return t('summary:billingApprovalPreviewText', { cadence: 2, context: isAPOrNetNew ? 'estimated' : '' })
  }

  if (allAmountsSame(installments) && !isNpuf) {
    return t('summary:sameInstallment', {
      context: hasActivePPD ? 'ppd' : undefined,
      installment: formatMoney(amountDueToday.amount),
    })
  }

  const restOfInstallments = installments.slice(1)
  const restOfInstallmentsEqual = allAmountsSame(restOfInstallments)
  const idx = getHighestAmountIndex(restOfInstallments)
  const highestAmount = restOfInstallments[idx]

  if (allAmountsSame([amountDueToday, highestAmount])) {
    return t('summary:sameInstallment', {
      context: hasActivePPD ? 'ppdUpTo' : 'upTo',
      installment: formatMoney(highestAmount.amount),
    })
  }

  const pufContext = restOfInstallmentsEqual ? '' : 'upTo'
  const ppdPufContext = restOfInstallmentsEqual ? 'ppd' : 'ppdUpTo'

  const context = hasActivePPD ? ppdPufContext : pufContext

  return t('summary:puf', {
    context,
    installment: formatMoney(highestAmount.amount),
    amountDueToday: formatMoney(amountDueToday.amount),
  })
}

// Note: includes zero dollar payment schedule
export const useExperimentPaymentScheduleList = (): CheckoutPaymentSchedule[] => {
  const paymentSchedules = useCalculatedPaymentSchedules()
  const { firstPaymentType } = useCheckoutState()
  const isNpuf = firstPaymentType === FirstPaymentType.NoPaymentUpfront
  const zeroMoney = money('0.00', paymentSchedules[0]?.amount?.currency)
  const dateToday = new Date().toDateString()
  const zeroMoneyPaymentSchedule: CheckoutPaymentSchedule = { amount: zeroMoney, installmentDueDate: dateToday }

  return [...(isNpuf ? [zeroMoneyPaymentSchedule] : []), ...paymentSchedules]
}
